import type { AgentTool, ToolEffects } from "@apholdings/jensen-agent-core";

/**
 * Production tool-effect registry and validation.
 *
 * Every built-in tool must declare its effects on the tool object (the
 * `effects` field). This module centralizes the canonical expectations and
 * provides a validation used both by diagnostics and by tests that fail when a
 * new production tool is added without effect metadata.
 */

export class UndeclaredEffectsError extends Error {
	readonly toolName: string;
	constructor(toolName: string) {
		super(`tool '${toolName}' has no declared effects`);
		this.name = "UndeclaredEffectsError";
		this.toolName = toolName;
	}
}

/** Tools that a policy engine must always treat conservatively if effects are unknown. */
export const REQUIRES_EFFECTS = true;

/**
 * Validate that every tool in the list declares effects. Returns the list of
 * undeclared tool names, or throws when `strict` is set.
 */
export function validateToolEffects<T extends AgentTool>(tools: T[], strict = false): string[] {
	const undeclared: string[] = [];
	for (const tool of tools) {
		if (!tool.effects) undeclared.push(tool.name);
	}
	if (strict && undeclared.length) throw new UndeclaredEffectsError(undeclared[0]);
	return undeclared;
}

/** Canonical effect metadata for each built-in production tool. */
export const PRODUCTION_TOOL_EFFECTS: Record<string, ToolEffects> = {
	read: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	grep: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	find: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	ls: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	todo_read: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "external", resource: "todo-runtime" }],
	},
	todo_write: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: false,
		scopes: [{ kind: "external", resource: "todo-runtime" }],
	},
	todo_update: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: false,
		scopes: [{ kind: "external", resource: "todo-runtime" }],
	},
	memory_write: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: false,
		scopes: [{ kind: "external", resource: "memory" }],
	},
	retrieve_evidence: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "external", resource: "evidence-archive" }],
	},
	edit: {
		readsWorkspace: true,
		writesWorkspace: true,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "workspace" }],
	},
	write: {
		readsWorkspace: false,
		writesWorkspace: true,
		createsFiles: true,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "workspace" }],
	},
	bash: {
		readsWorkspace: true,
		writesWorkspace: true,
		createsFiles: true,
		deletesFiles: true,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: true,
		mutatesGit: true,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: true,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "unknown" }],
	},
	powershell: {
		readsWorkspace: true,
		writesWorkspace: true,
		createsFiles: true,
		deletesFiles: true,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: true,
		mutatesGit: true,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: true,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "unknown" }],
	},
	process_manager: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "process" }],
	},
	web_search: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: true,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "network" }],
	},
	web_fetch: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: true,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "network" }],
	},
	deep_research: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: true,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "network" }],
	},
	web_research_status: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [],
	},
	// Workspace intelligence (1.7.0)
	workspace_search: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	workspace_search_lexical: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	workspace_search_semantic: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	workspace_search_symbols: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	workspace_retrieval_status: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	workspace_index_refresh: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: true,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: false,
		scopes: [{ kind: "workspace" }],
	},
	// Language Server Protocol (LSP) tools — all read-only / parallel-safe.
	lsp_definition: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_references: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_implementations: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_hover: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_diagnostics: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_document_symbols: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_workspace_symbols: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_rename_preview: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	lsp_status: {
		readsWorkspace: true,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "workspace" }],
	},
	// Background-job registry tools — process lifecycle, mutating-ish, serial.
	job_start: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "process" }],
	},
	job_status: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "process" }],
	},
	job_list: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "process" }],
	},
	job_logs: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: false,
		parallelSafe: true,
		scopes: [{ kind: "process" }],
	},
	job_stop: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "process" }],
	},
	job_restart: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: true,
		startsPersistentProcesses: true,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "process" }],
	},
	job_adopt: {
		readsWorkspace: false,
		writesWorkspace: false,
		createsFiles: false,
		deletesFiles: false,
		executesProcesses: false,
		startsPersistentProcesses: false,
		accessesNetwork: false,
		mutatesGit: false,
		mutatesExternalState: false,
		handlesSecrets: false,
		potentiallyDestructive: false,
		requiresExclusiveWorkspaceLease: true,
		parallelSafe: false,
		scopes: [{ kind: "process" }],
	},
};
